document.addEventListener("DOMContentLoaded", function () {
  let isFetching = false;
  let observer = null;
  let productContainer = null;
  let sentinel = null;
  let watchdog = null;
  let scrollListenerAttached = false;
  const SCROLL_THRESHOLD_PCT = 15;
  const SCROLL_DEBOUNCE_MS = 50;
  const SCROLL_DELTA = 50;
  let lastScrollY = 0;
  let debounceTO = null;

  // --- Hook de history: dispara 'locationchange' en push/replaceState y popstate
  (function () {
    const _push = history.pushState;
    const _replace = history.replaceState;
    history.pushState = function () {
      const r = _push.apply(this, arguments);
      window.dispatchEvent(new Event("locationchange"));
      return r;
    };
    history.replaceState = function () {
      const r = _replace.apply(this, arguments);
      window.dispatchEvent(new Event("locationchange"));
      return r;
    };
    window.addEventListener("popstate", () => {
      window.dispatchEvent(new Event("locationchange"));
    });
  })();

  const getContainer = () =>
    document.querySelector("#product-grid product-list") ||
    document.querySelector("product-list") ||
    document.querySelector("#product-grid .product-list") ||
    document.querySelector(".product-list");

  function ensureSentinel() {
    if (!productContainer) return;
    if (!sentinel || !sentinel.isConnected) {
      sentinel = document.createElement("div");
      sentinel.id = "infinite-sentinel";
      sentinel.setAttribute("aria-hidden", "true");
      sentinel.style.cssText = "display:block;width:100%;height:1px;margin-top:1px;";
      productContainer.appendChild(sentinel);
    }
  }

  function getCurrentPage() {
    return parseInt(productContainer?.dataset.page || "1", 10);
  }
  function setCurrentPage(page) {
    if (productContainer) productContainer.dataset.page = String(page);
  }

  function removeScrollListener() {
    if (scrollListenerAttached) {
      window.removeEventListener("scroll", onWindowScroll, { passive: true });
      scrollListenerAttached = false;
    }
  }
  function addScrollListener() {
    if (!scrollListenerAttached) {
      window.addEventListener("scroll", onWindowScroll, { passive: true });
      scrollListenerAttached = true;
    }
  }
  function onWindowScroll() {
    if (isFetching) return;
    const y = window.scrollY || document.documentElement.scrollTop || 0;
    if (Math.abs(y - lastScrollY) < SCROLL_DELTA) return;
    lastScrollY = y;
    if (debounceTO) clearTimeout(debounceTO);
    debounceTO = setTimeout(() => {
      const h = window.innerHeight;
      const docH = document.documentElement.scrollHeight;
      const pct = ((y + h) / docH) * 100;
      if (pct >= SCROLL_THRESHOLD_PCT) loadMoreProducts();
    }, SCROLL_DEBOUNCE_MS);
  }

  function initObserver() {
    if (!productContainer) return;
    ensureSentinel();

    if (observer) observer.disconnect();

    // CLAVE: usa viewport siempre para evitar roots falsos por barras sticky/overflow
    try {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) loadMoreProducts();
        },
        { root: null, rootMargin: "1000px 0px", threshold: 0 }
      );
      observer.observe(sentinel);
    } catch {
      observer = null;
    }

    // Fallback por si IO no existe o el sentinel no intersecta por cualquier motivo
    addScrollListener();
  }

  function boot() {
    const c = getContainer();
    if (!c) return false;

    // Si cambió el nodo, resetea
    if (productContainer !== c) {
      productContainer = c;
      if (observer) observer.disconnect();
      observer = null;
      sentinel = null;
      removeScrollListener();
    }

    if (!productContainer.dataset.page) setCurrentPage(1);
    ensureSentinel();
    initObserver();

    // autoload si la página queda corta
    if (document.documentElement.scrollHeight <= window.innerHeight * 1.2) {
      loadMoreProducts();
    }
    return true;
  }

  // Arranque inicial + watchdog
  const started = boot();
  if (!started) {
    const bootMo = new MutationObserver(() => {
      if (boot()) bootMo.disconnect();
    });
    bootMo.observe(document.body, { childList: true, subtree: true });
  }

  // Watchdog: valida cada 500ms que todo siga enganchado (por si el tema re-renderiza sin avisar)
  function startWatchdog() {
    if (watchdog) clearInterval(watchdog);
    watchdog = setInterval(() => {
      const exists = !!getContainer();
      if (!exists || !productContainer?.isConnected) {
        boot();
        return;
      }
      if (!sentinel || !sentinel.isConnected) {
        ensureSentinel();
        initObserver();
      }
    }, 500);
  }
  startWatchdog();

  // Rebind explícito según cambian filtros o la URL
  window.addEventListener("locationchange", () => {
    setTimeout(() => boot(), 50);
  });
  document.addEventListener("facet:update", () => {
    setTimeout(() => {
      const newContainer = getContainer();
      if (!newContainer) return;
      if (productContainer !== newContainer) {
        productContainer = newContainer;
      }
      setCurrentPage(1);
      ensureSentinel();
      initObserver();
      if (typeof window.initSwipeSlider === "function") window.initSwipeSlider();
    }, 300);
  });

  function loadMoreProducts() {
    if (isFetching || !productContainer) return;
    isFetching = true;

    const nextPage = getCurrentPage() + 1;
    const url = new URL(window.location.href);
    url.searchParams.set("page", nextPage);

    fetch(url.href, {
      headers: { "X-Requested-With": "XMLHttpRequest", Accept: "text/html" },
      cache: "no-store",
    })
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, "text/html");

        // MISMO selector que en prod para evitar sorpresas
        const newProducts = doc.querySelectorAll("product-list .product-card");

        // Si no hay más productos —> apagamos IO y scroll
        if (!newProducts.length) {
          if (observer) observer.disconnect();
          removeScrollListener();
          isFetching = false;
          return;
        }

        const frag = document.createDocumentFragment();
        let added = 0;

        newProducts.forEach((card) => {
          // Usamos data-product-id (product.id, siempre único) para dedup, no data-id (SKU puede estar vacío o repetido)
          const pid = card.getAttribute("data-product-id") || card.getAttribute("data-id");
          const attr = card.getAttribute("data-product-id") ? "data-product-id" : "data-id";
          if (!pid || !productContainer.querySelector(`[${attr}="${pid}"]`)) {
            frag.appendChild(card);
            added++;
          }
        });

        if (added > 0) {
          requestAnimationFrame(() => {
            ensureSentinel();

            const newNodes = Array.from(frag.children);
            // Inserta antes del sentinel para mantener el sentinel siempre al final
            productContainer.insertBefore(frag, sentinel);

            // Ejecuta <script> embebidos en los nuevos nodos
            newNodes.forEach((root) => {
              root.querySelectorAll("script").forEach((old) => {
                const s = document.createElement("script");
                for (const attr of old.attributes) s.setAttribute(attr.name, attr.value);
                s.textContent = old.textContent;
                old.replaceWith(s);
              });
            });

            // Re-init global como en prod
            if (typeof window.initSwipeSlider === "function") {
              window.initSwipeSlider();
            }

            // Lazy
            if (window.lazySizes?.loader) {
              newNodes.forEach((node) => {
                node.querySelectorAll("img.lazyload, source[data-srcset]").forEach((el) => {
                  window.lazySizes.loader.unveil(el);
                });
              });
            } else {
              newNodes.forEach((node) => {
                node.querySelectorAll("img[data-src]").forEach((img) => (img.src = img.dataset.src));
                node.querySelectorAll("source[data-srcset]").forEach((src) => (src.srcset = src.dataset.srcset));
              });
            }

            // Señales suaves
            window.dispatchEvent(new Event("resize"));
            window.dispatchEvent(new Event("scroll"));

            // Estado
            setCurrentPage(nextPage);

            // Re-observar por si el sentinel se movió
            initObserver();

            document.dispatchEvent(
              new CustomEvent("productsLoaded", { detail: { count: added, page: nextPage } })
            );
          });
        } else {
          // No añadimos nada — avanzamos página por si acaso y paramos
          setCurrentPage(nextPage);
          if (observer) observer.disconnect();
          removeScrollListener();
        }
      })
      .catch((e) => console.error("InfiniteScroll fetch error:", e))
      .finally(() => {
        isFetching = false;
      });
  }

  // Fallback por mutaciones gordas en el grid
  const mo = new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.type === "childList") {
        const touched =
          [...m.addedNodes, ...m.removedNodes].some(
            (n) =>
              n.nodeType === 1 &&
              (n.matches?.("product-list,.product-list") || n.querySelector?.("product-list,.product-list"))
          );
        if (touched) {
          setTimeout(boot, 30);
          break;
        }
      }
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });
});
